import React from "react";
import { motion } from "framer-motion";
import { fade } from "../story";

/**
 * Hand-authored SVG diagrams for the NR NTN testbed write-up.
 *
 * Drawn rather than screenshotted so they stay legible at any width and pick up
 * the site palette directly. Colours below mirror tailwind.config.js.
 */

const C = {
  stroke: "#3f3f46",
  box: "#18181b",
  boxStroke: "#27272a",
  text: "#fafafa",
  dim: "#a1a1aa",
  faint: "#71717a",
  accent: "#7dd3fc",
  accentFill: "rgba(125,211,252,0.08)",
  accentStroke: "rgba(125,211,252,0.45)",
};

/** Arrowhead defs. `id` is prefixed per-diagram so markers never collide. */
function Defs({ id }) {
  return (
    <defs>
      <marker
        id={`${id}-arrow`}
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={C.stroke} />
      </marker>
      <marker
        id={`${id}-arrow-accent`}
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={C.accent} />
      </marker>
    </defs>
  );
}

function Box({ x, y, w, h, label, sub, sub2, accent = false }) {
  // Coerce: callers pass these as JSX string attributes as well as numbers, and
  // string arithmetic would put the labels off-canvas.
  const [X, Y, W, H] = [x, y, w, h].map(Number);
  const cx = X + W / 2;
  const lines = [label, sub, sub2].filter(Boolean).length;
  const top = Y + H / 2 - (lines - 1) * 8 + 5;
  return (
    <g>
      <rect
        x={X}
        y={Y}
        width={W}
        height={H}
        rx="6"
        fill={accent ? C.accentFill : C.box}
        stroke={accent ? C.accentStroke : C.boxStroke}
      />
      <text
        x={cx}
        y={top}
        textAnchor="middle"
        fill={accent ? C.accent : C.text}
        fontSize="13.5"
        fontWeight="500"
      >
        {label}
      </text>
      {sub && (
        <text x={cx} y={top + 16} textAnchor="middle" fill={C.faint} fontSize="11">
          {sub}
        </text>
      )}
      {sub2 && (
        <text x={cx} y={top + 31} textAnchor="middle" fill={C.faint} fontSize="11">
          {sub2}
        </text>
      )}
    </g>
  );
}

/** A small lettered capture-point marker. */
function Marker({ x, y, letter }) {
  return (
    <g>
      <circle cx={x} cy={y} r="10" fill={C.box} stroke={C.accentStroke} />
      <text x={x} y={y + 4} textAnchor="middle" fill={C.accent} fontSize="11" fontWeight="600">
        {letter}
      </text>
    </g>
  );
}

function Label({ x, y, children, anchor = "middle", size = 11, fill = C.dim }) {
  return (
    <text x={x} y={y} textAnchor={anchor} fill={fill} fontSize={size}>
      {children}
    </text>
  );
}

/**
 * Wrapper giving each diagram a bordered panel and a caption. `minWidth` lets
 * wide diagrams scroll horizontally on a phone rather than shrink to illegible.
 */
function Diagram({ label, caption, viewBox, minWidth = "34rem", children }) {
  return (
    <motion.figure variants={fade}>
      <div className="rounded-lg border border-border bg-surface/50 p-4 md:p-6 overflow-x-auto">
        <svg
          viewBox={viewBox}
          role="img"
          aria-label={label}
          className="h-auto w-full font-sans"
          style={{ minWidth }}
        >
          {children}
        </svg>
      </div>
      {caption && (
        <figcaption className="mt-2.5 text-sm text-muted-foreground leading-relaxed">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

/* ══════════════════ 1 · the chain ══════════════════ */

export function ChainDiagram() {
  const id = "chain";
  const a = `url(#${id}-arrow)`;
  return (
    <Diagram
      label="Signal chain from the 5G core through the gNB and GNU Radio channel to the UE"
      viewBox="0 0 860 310"
      caption="One flowgraph carries both directions, so the GNU Radio scheduler provides the lockstep. The downlink channel is applied before the UE sees the signal and the uplink channel after it transmits, which is the only physically meaningful ordering."
    >
      <Defs id={id} />

      {/* flowgraph enclosure */}
      <rect
        x="362"
        y="30"
        width="194"
        height="242"
        rx="8"
        fill="none"
        stroke={C.accentStroke}
        strokeDasharray="4 4"
      />
      <Label x="459" y="50" fill={C.accent}>
        One GNU Radio flowgraph
      </Label>

      <Box x="10" y="126" w="110" h="54" label="Open5GS" sub="5G core" />
      <Box x="165" y="126" w="150" h="54" label="OCUDU gNB" sub="CU-CP · CU-UP · DU" />
      <Box
        x="376"
        y="72"
        w="166"
        h="56"
        label="Downlink channel"
        sub="delay · Doppler · C/N · fading"
      />
      <Box
        x="376"
        y="186"
        w="166"
        h="56"
        label="Uplink channel"
        sub="delay · Doppler · C/N · fading"
      />
      <Box x="600" y="126" w="150" h="54" label="OAI UE" sub="nr-uesoftmodem" />

      {/* core <-> gNB */}
      <line x1="120" y1="153" x2="165" y2="153" stroke={C.stroke} markerEnd={a} markerStart={a} />
      <Label x="142" y="144" size="10" fill={C.faint}>
        NG
      </Label>

      {/* downlink path */}
      <polyline
        points="315,140 336,140 336,100 376,100"
        fill="none"
        stroke={C.stroke}
        markerEnd={a}
      />
      <polyline
        points="542,100 576,100 576,140 600,140"
        fill="none"
        stroke={C.stroke}
        markerEnd={a}
      />
      {/* uplink path */}
      <polyline
        points="600,166 576,166 576,214 542,214"
        fill="none"
        stroke={C.stroke}
        markerEnd={a}
      />
      <polyline
        points="376,214 336,214 336,166 315,166"
        fill="none"
        stroke={C.stroke}
        markerEnd={a}
      />

      <Marker x="356" y="100" letter="A" />
      <Marker x="562" y="100" letter="C" />
      <Marker x="675" y="126" letter="B" />

      <Label x="459" y="262" size="10.5" fill={C.faint}>
        bridge: ZeroMQ ↔ rfsimulator, owns the timing budget
      </Label>

      <Label x="430" y="298" size="10.5" fill={C.faint}>
        A and C are a matched capture pair, pre- and post-channel · B is where your own receiver
        goes
      </Label>
    </Diagram>
  );
}

/* ══════════════════ 2 · the generator ══════════════════ */

export function GeneratorDiagram() {
  const id = "gen";
  const a = `url(#${id}-arrow)`;
  const artifacts = [
    [18, "gNB NTN config", "sample rate, K_offset, timers"],
    [96, "SIB19 ephemeris", "broadcast on air to the UE"],
    [174, "UE position file", "so it derives its own advance"],
    [252, "Channel delay + Doppler", "what the flowgraph applies"],
  ];
  return (
    <Diagram
      label="One geometry generating every configuration artifact in a single run"
      viewBox="0 0 920 330"
      minWidth="38rem"
      caption="Because a single run emits all four, the timing advance the UE derives from the broadcast ephemeris and the delay the channel applies cannot drift apart. Hand-editing any one of them silently breaks the guarantee."
    >
      <Defs id={id} />

      <Box
        x="10"
        y="120"
        w="170"
        h="90"
        label="Geometry"
        sub="LEO state vector"
        sub2="or GEO longitude"
        accent
      />
      <line x1="180" y1="165" x2="238" y2="165" stroke={C.stroke} markerEnd={a} />

      <Box x="240" y="135" w="130" h="60" label="One generator" sub="run" />

      {artifacts.map(([y, label, sub]) => (
        <g key={label}>
          <path
            d={`M 370 165 C 420 165, 420 ${y + 26}, 468 ${y + 26}`}
            fill="none"
            stroke={C.stroke}
            markerEnd={a}
          />
          <Box x="470" y={y} w="250" h="52" label={label} sub={sub} />
        </g>
      ))}

      {/* consistency check tying SIB19 to the applied delay */}
      <polyline
        points="720,122 780,122 780,278 720,278"
        fill="none"
        stroke={C.accentStroke}
        strokeDasharray="4 4"
      />
      <Label x="790" y="186" anchor="start" size="10.5" fill={C.accent}>
        SIB19-derived TA
      </Label>
      <Label x="790" y="202" anchor="start" size="10.5" fill={C.accent}>
        vs applied delay:
      </Label>
      <Label x="790" y="218" anchor="start" size="10.5" fill={C.accent}>
        agree to 3 ns
      </Label>
    </Diagram>
  );
}

/* ══════════════════ 3 · the timing contract ══════════════════ */

export function TimingDiagram() {
  const id = "timing";
  const a = `url(#${id}-arrow-accent)`;
  return (
    <Diagram
      label="Timing contract between the downlink delay, uplink delay and the UE timing advance"
      viewBox="0 0 860 290"
      caption="The uplink answer to a grant lands in the slot the scheduler expects only when the total transport delay equals the advance the UE applied, and the round trip fits inside the scheduling offset. Generate the scenario consistently and the residual is zero."
    >
      <Defs id={id} />

      {/* lifelines */}
      <line x1="60" y1="70" x2="800" y2="70" stroke={C.boxStroke} />
      <line x1="60" y1="215" x2="800" y2="215" stroke={C.boxStroke} />
      <Label x="50" y="74" anchor="end" size="12" fill={C.text}>
        gNB
      </Label>
      <Label x="50" y="219" anchor="end" size="12" fill={C.text}>
        UE
      </Label>

      {/* round-trip bracket */}
      <polyline points="150,42 150,32 780,32 780,42" fill="none" stroke={C.stroke} />
      <Label x="465" y="24" size="11.5" fill={C.dim}>
        RTT ≤ K_offset + k₂
      </Label>

      {/* downlink grant */}
      <line x1="150" y1="70" x2="428" y2="215" stroke={C.accent} markerEnd={a} />
      <Label x="256" y="135" anchor="end" size="11.5" fill={C.accent}>
        D_dl
      </Label>
      <Label x="150" y="60" size="10.5" fill={C.faint}>
        grant, slot n
      </Label>

      {/* UE processing */}
      <line x1="428" y1="215" x2="502" y2="215" stroke={C.stroke} strokeWidth="2.5" />
      <Label x="465" y="234" size="10.5" fill={C.faint}>
        k₂
      </Label>

      {/* uplink response */}
      <line x1="502" y1="215" x2="780" y2="70" stroke={C.accent} markerEnd={a} />
      <Label x="676" y="135" anchor="start" size="11.5" fill={C.accent}>
        D_ul
      </Label>
      <Label x="780" y="60" anchor="end" size="10.5" fill={C.faint}>
        lands in slot n + k₂ + K_offset
      </Label>

      {/* the contract */}
      <Label x="465" y="268" size="12.5" fill={C.text}>
        the UE transmits TA_ue early, so D_dl + D_ul = TA_ue puts this in the expected slot
      </Label>
    </Diagram>
  );
}

/* ══════════════════ 4 · impairment ordering ══════════════════ */

export function ImpairmentDiagram() {
  const id = "imp";
  const a = `url(#${id}-arrow)`;
  const blocks = [
    [40, "Fading taps", "×"],
    [200, "Shadowing", "×"],
    [360, "Doppler rotation", "e^jθ"],
    [520, "Interference", "+"],
    [680, "Thermal noise", "+"],
  ];
  return (
    <Diagram
      label="Order of the impairment chain, multiplicative terms before the Doppler rotation and additive terms after"
      viewBox="0 0 880 195"
      caption="Each fading tap's total delay selects its own row of the fractional-delay bank, and the rows are summed into one composite kernel, so a faded stream passes through exactly one interpolator and keeps its 0.002 dB passband flatness."
    >
      <Defs id={id} />

      {/* grouping brackets */}
      <polyline points="40,52 40,44 325,44 325,52" fill="none" stroke={C.boxStroke} />
      <Label x="182" y="36" size="10.5" fill={C.faint}>
        multiply the wanted signal
      </Label>
      <polyline points="520,52 520,44 805,44 805,52" fill="none" stroke={C.boxStroke} />
      <Label x="662" y="36" size="10.5" fill={C.faint}>
        add at the receiver front end
      </Label>

      {blocks.map(([x, label, sub], i) => (
        <g key={label}>
          <Box x={x} y="60" w="125" h="58" label={label} sub={sub} accent={i === 2} />
          {i < blocks.length - 1 && (
            <line
              x1={x + 125}
              y1="89"
              x2={x + 155}
              y2="89"
              stroke={C.stroke}
              markerEnd={a}
            />
          )}
        </g>
      ))}

      <line x1="6" y1="89" x2="36" y2="89" stroke={C.stroke} markerEnd={a} />
      <line x1="805" y1="89" x2="850" y2="89" stroke={C.stroke} markerEnd={a} />

      <Label x="440" y="152" size="11" fill={C.dim}>
        the channel config can change any of these, but it cannot move a delay or a Doppler, so the
        timing contract stays untouched while you sweep
      </Label>
    </Diagram>
  );
}
